import { useEffect, useState, useMemo } from 'react';
import { useMeeting } from '../meeting/meetingProviderContextDef';

/**
 *
 * @param {
 * {
 *  interactionId?: string,
 *  id?: string,
 *  displayName?: string,
 *  characterRole?: string,
 *  characterMode?: "text" | "co_pilot" | "auto_pilot" | "vision_pilot",
 *  knowledgeBases?: string[],
 *  language?: string,
 * },
 * {
 *  onCharacterMessage?: Function,
 *  onData?: Function,
 *  onError?: Function,
 *  onUserMessage?: Function,
 *  onStreamDisabled?: Function,
 *  onStreamEnabled?: Function,
 *  onMediaStatusChanged?: Function,
 *  onVideoQualityChanged?: Function,
 * }} options
 *
 * @returns {
 *
 *  displayName: string,
 *  webcamStream: Stream,
 *  micStream: Stream,
 *  webcamOn: boolean,
 *  micOn: boolean,
 *  isActiveSpeaker: string,
 *
 *  enableMic: Function,
 *  disableMic: Function,
 *  enableWebcam: Function,
 *  disableWebcam: Function,
 *
 *  join: Function,
 *  leave: Function,
 *  sendMessage: Function
 *  interrupt: Function
 *
 *}
 *
 */
const useCharacter = (
  {
    interactionId,
    // OR
    id,
    characterMode,
    displayName,
    characterRole,
    knowledgeBases,
    language,
    metaData
  },
  {
    onCharacterStateChanged = () => {},
    onCharacterMessage = () => {},
    onUserMessage = () => {},
    onData = () => {},
    onError = () => {},

    onCharacterJoined = () => {},

    onCharacterLeft = () => {},
    onStreamEnabled = () => {},
    onStreamDisabled = () => {},
    onMediaStatusChanged = () => {},
    onVideoQualityChanged = () => {}
  } = {}
) => {
  const [webcamStream, setwebcamStream] = useState(null);
  const [micStream, setMicStream] = useState(null);

  const mMeeting = useMeeting();

  /**
   * @type {Character}
   */
  const { character, activeSpeakerId } = useMemo(() => {
    let chars = Array.from(mMeeting.characters.values());
    const activeSpeakerId = mMeeting?.activeSpeakerId;

    return {
      character:
        chars.find(chars => chars.interactionId == interactionId) ||
        mMeeting.characters.get(id) ||
        mMeeting.meeting.createCharacter({
          interactionId,
          // OR
          displayName,
          id,
          characterRole,
          characterMode,
          knowledgeBases,
          language,
          metaData
        }),
      activeSpeakerId
    };
  }, [
    mMeeting.characters,
    interactionId,
    id,
    characterMode,
    displayName,
    characterRole,
    knowledgeBases,
    language,
    metaData
  ]);

  const [webcamOn, setWebcamOn] = useState(character?.webcamOn);
  const [micOn, setMicOn] = useState(character?.micOn);
  const [characterState, setCharacterState] = useState(null);

  const setTrack = stream => {
    if (stream.track.readyState === 'live') {
      switch (stream.kind) {
        case 'video':
          if (webcamStream != null) {
            setwebcamStream(null);
          }
          setwebcamStream(stream);
          break;
        case 'audio':
          if (micStream != null) {
            setMicStream(null);
          }
          setMicStream(stream);
          break;
        default:
          break;
      }
    }
  };

  const unSetTrack = stream => {
    switch (stream.kind) {
      case 'video':
        setwebcamStream(null);
        break;
      case 'audio':
        setMicStream(null);
        break;
      default:
        break;
    }
  };

  const _handleCharacterStateChanged = d => {
    const { status } = d;
    setCharacterState(status);
    onCharacterStateChanged(d);
  };

  const _handleCharacterMessage = d => {
    onCharacterMessage(d);
  };

  const _handleUserMessage = d => {
    onUserMessage(d);
  };

  const _handleOnData = (topic, d) => {
    onData(topic, d);
  };

  const _handleOnError = d => {
    onError(d);
  };

  const _handleCharacterJoined = () => {
    onCharacterJoined();
  };

  const _handleCharacterLeft = () => {
    onCharacterLeft();
  };

  const _handleStreamEnabled = stream => {
    setTrack(stream);
    onStreamEnabled(stream);
  };

  const _handleStreamDisabled = stream => {
    unSetTrack(stream);
    onStreamDisabled(stream);
  };

  const _handleVideoQualityChanged = data => {
    const { peerId, prevQuality, currentQuality } = data;
    onVideoQualityChanged({ peerId, prevQuality, currentQuality });
  };

  const _handleMediaStatusChanged = data => {
    const { kind, peerId, newStatus } = data;
    if (kind == 'audio') {
      setMicOn(newStatus);
    } else if (kind == 'video') {
      setWebcamOn(newStatus);
    }
    onMediaStatusChanged({ kind, peerId, newStatus });
  };

  const enableMic = () => {
    character?.enableMic();
  };
  const disableMic = () => {
    character?.disableMic();
  };
  const enableWebcam = () => {
    character?.enableWebcam();
  };
  const disableWebcam = () => {
    character?.disableWebcam();
  };
  const join = async () => {
    await character?.join();
  };
  const sendMessage = async d => {
    await character?.sendMessage(d);
  };
  const interrupt = async () => {
    await character?.interrupt();
  };
  const leave = async () => {
    await character?.leave();
  };

  useEffect(() => {
    const streams = character?.streams;

    if (streams) {
      streams.forEach(stream => {
        setTrack(stream);
      });
    }

    if (character?.micOn) {
      setMicOn(micOn);
    }

    if (character?.webcamOn) {
      setWebcamOn(webcamOn);
    }

    character?.on('character-state-changed', _handleCharacterStateChanged);

    character?.on('character-message', _handleCharacterMessage);

    character?.on('user-message', _handleUserMessage);

    character?.on('data', _handleOnData);

    character?.on('error', _handleOnError);

    character?.on('stream-enabled', _handleStreamEnabled);

    character?.on('stream-disabled', _handleStreamDisabled);

    character?.on('media-status-changed', _handleMediaStatusChanged);

    character?.on('video-quality-changed', _handleVideoQualityChanged);

    character?.on('character-joined', _handleCharacterJoined);

    character?.on('character-left', _handleCharacterLeft);

    return () => {
      character?.off('character-state-changed', _handleCharacterStateChanged);

      character?.off('character-message', _handleCharacterMessage);

      character?.off('user-message', _handleUserMessage);

      character?.off('data', _handleOnData);

      character?.off('error', _handleOnError);

      character?.off('stream-enabled', _handleStreamEnabled);

      character?.off('stream-disabled', _handleStreamDisabled);

      character?.off('media-status-changed', _handleMediaStatusChanged);

      character?.off('video-quality-changed', _handleVideoQualityChanged);

      character?.off('character-joined', _handleCharacterJoined);

      character?.off('character-left', _handleCharacterLeft);
    };
  }, [character]);

  return {
    displayName: character?.displayName,
    webcamStream,
    micStream,
    webcamOn: webcamOn || !!webcamStream,
    micOn: micOn || !!micStream,
    isActiveSpeaker: activeSpeakerId === id,

    interactionId: character?.interactionId,
    id: character?.id,
    characterMode: character?.characterMode,
    characterState: characterState || character?.characterState,
    knowledgeBases: character?.knowledgeBases,
    language: character?.language,

    enableMic,
    disableMic,
    enableWebcam,
    disableWebcam,

    join,
    leave,
    sendMessage,
    interrupt
  };
};

export default useCharacter;
